import {
  AGENT_MODEL_TIERS,
  AGENT_TOOLS,
  type AgentModelTier,
  type AgentSpec,
  type AgentTool,
  type AvailableTool,
  type AvailableTools,
  DEFAULT_MAX_REQUESTS_PER_RUN,
  LOCAL_TOOL_CATALOG,
  LOCAL_TOOL_GROUP_LABELS,
  LOCAL_TOOL_GROUPS,
} from '@sbc/domain-models/agents';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { MarkdownEditor } from '@/components/markdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAvailableTools } from '@/lib/api/available-tools';
import { useModelTiers } from '@/lib/api/model-tiers';

interface ToolGroupModel {
  readonly key: string;
  readonly title: string;
  readonly tools: readonly AvailableTool[];
}

function pushToGroup(groups: Map<string, AvailableTool[]>, key: string, tool: AvailableTool) {
  const existing = groups.get(key);
  if (existing) {
    existing.push(tool);
  } else {
    groups.set(key, [tool]);
  }
}

// The picker's group order: the eight local groups first, in their declared order, then one
// group per MCP server. `handoff` comes from the local catalog while its peers arrive as
// data-defined `agent.*` refs, so the agents group is the one place the two sources merge.
function buildToolGroups(available: AvailableTools, ownSlug: string): ToolGroupModel[] {
  const local = new Map<string, AvailableTool[]>();
  for (const ref of available.local) {
    const entry = LOCAL_TOOL_CATALOG[ref];
    pushToGroup(local, entry.group, { ref, label: entry.label });
  }
  for (const peer of available.agents) {
    if (peer.ref !== `agent.${ownSlug}`) {
      pushToGroup(local, 'agents', peer);
    }
  }

  const servers = new Map<string, AvailableTool[]>();
  for (const tool of available.mcp) {
    if (tool.connectionId) {
      pushToGroup(servers, tool.connectionId, tool);
    }
  }

  return [
    ...LOCAL_TOOL_GROUPS.flatMap((group): ToolGroupModel[] => {
      const tools = local.get(group);
      return tools?.length ? [{ key: group, title: LOCAL_TOOL_GROUP_LABELS[group], tools }] : [];
    }),
    ...[...servers.entries()].map(([connectionId, tools]) => ({
      key: connectionId,
      title: connectionId,
      tools,
    })),
  ];
}

function ToolGroup({
  title,
  tools,
  enabledTools,
  onToggleTool,
  onToggleAll,
}: {
  title: string;
  tools: readonly AvailableTool[];
  enabledTools: AgentTool[];
  onToggleTool: (tool: AgentTool, on: boolean) => void;
  onToggleAll: (tools: AgentTool[], on: boolean) => void;
}) {
  const refs = tools.map((tool) => tool.ref);
  // Open on mount only where something is already picked, so editing an agent shows what it
  // has without the operator hunting through eight collapsed headers. Deliberately initial
  // state, not derived: once open, a deselect-all must not slam the group shut mid-edit.
  const [open, setOpen] = useState(() => refs.some((ref) => enabledTools.includes(ref)));
  const selectedCount = refs.filter((ref) => enabledTools.includes(ref)).length;
  const allSelected = selectedCount === refs.length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="flex items-center gap-1 text-sm leading-none font-medium"
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {title}
          <span className="text-[var(--color-muted-foreground)]">
            ({selectedCount}/{refs.length})
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onToggleAll(refs, !allSelected)}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </Button>
      </div>
      {open &&
        tools.map((tool) => (
          <label key={tool.ref} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={enabledTools.includes(tool.ref)}
              onChange={(event) => onToggleTool(tool.ref, event.target.checked)}
            />
            {tool.label}
          </label>
        ))}
    </div>
  );
}

const TIER_LABELS: Record<AgentModelTier, string> = {
  large: 'Large',
  medium: 'Medium',
  small: 'Small',
  'claude-large': 'Claude large',
  'claude-medium': 'Claude medium',
  'claude-small': 'Claude small',
  eval: 'Eval only',
};

const EMPTY_SPEC: AgentSpec = {
  slug: '',
  name: '',
  description: '',
  modelTier: 'medium',
  systemPrompt: '',
  voicePrompt: '',
  maxRequestsPerRun: DEFAULT_MAX_REQUESTS_PER_RUN,
  enabledTools: [],
};

interface AgentFormProps {
  initial?: AgentSpec;
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  onSubmit: (spec: AgentSpec) => void;
  onCancel: () => void;
}

export function AgentForm({
  initial,
  submitting,
  submitLabel,
  error,
  onSubmit,
  onCancel,
}: AgentFormProps) {
  const [spec, setSpec] = useState<AgentSpec>(initial ?? EMPTY_SPEC);
  const availableToolsQuery = useAvailableTools();
  const tiersQuery = useModelTiers();
  const boundModel = (tier: AgentModelTier) =>
    tiersQuery.data?.find((binding) => binding.tier === tier)?.modelId;

  const toggleTools = (tools: AgentTool[], on: boolean) => {
    const affected = new Set(tools);
    setSpec((prev) => ({
      ...prev,
      enabledTools: on
        ? [...prev.enabledTools, ...tools.filter((tool) => !prev.enabledTools.includes(tool))]
        : prev.enabledTools.filter((value) => !affected.has(value)),
    }));
  };

  const toggleTool = (tool: AgentTool, on: boolean) => toggleTools([tool], on);

  // A failed available-tools fetch still has to let an operator pick local tools, so fall back
  // to the compiled-in enum; only the data-defined agent and MCP refs are unavailable then.
  const toolGroups = buildToolGroups(
    availableToolsQuery.data ?? { local: [...AGENT_TOOLS], agents: [], mcp: [] },
    spec.slug,
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(spec);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          required
          maxLength={120}
          value={spec.name}
          onChange={(event) => setSpec((prev) => ({ ...prev, name: event.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          required
          maxLength={60}
          placeholder="asksbc"
          pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
          value={spec.slug}
          onChange={(event) => setSpec((prev) => ({ ...prev, slug: event.target.value }))}
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Routing key when this version is active. Lowercase letters, numbers and dashes.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          maxLength={2000}
          value={spec.description}
          onChange={(event) => setSpec((prev) => ({ ...prev, description: event.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="model">Model</Label>
        <Select
          value={spec.modelTier}
          onValueChange={(value) =>
            setSpec((prev) => ({
              ...prev,
              modelTier: AGENT_MODEL_TIERS.find((tier) => tier === value) ?? prev.modelTier,
            }))
          }
        >
          <SelectTrigger id="model">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AGENT_MODEL_TIERS.map((tier) => (
              <SelectItem key={tier} value={tier}>
                {TIER_LABELS[tier]}
                {boundModel(tier) ? ` — ${boundModel(tier)}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Which model each tier points at is set on the Models page.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="maxRequestsPerRun">Request budget per run</Label>
        <Input
          id="maxRequestsPerRun"
          type="number"
          required
          min={1}
          value={spec.maxRequestsPerRun}
          onChange={(event) =>
            setSpec((prev) => ({ ...prev, maxRequestsPerRun: event.target.valueAsNumber }))
          }
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          How many model requests one run of this agent may make before it aborts.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="systemPrompt">System prompt</Label>
        <MarkdownEditor
          id="systemPrompt"
          value={spec.systemPrompt}
          onChange={(value) => setSpec((prev) => ({ ...prev, systemPrompt: value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="voicePrompt">Voice prompt</Label>
        <MarkdownEditor
          id="voicePrompt"
          value={spec.voicePrompt}
          onChange={(value) => setSpec((prev) => ({ ...prev, voicePrompt: value }))}
          required={false}
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Optional voice/tone instructions, appended after the system prompt as a{' '}
          <code>&lt;voice&gt;</code> section at session start. Leave empty to omit.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Enabled tools</Label>
        {availableToolsQuery.isPending ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">Loading tools…</p>
        ) : (
          <div className="space-y-4">
            {toolGroups.map((group) => (
              <ToolGroup
                key={group.key}
                title={group.title}
                tools={group.tools}
                enabledTools={spec.enabledTools}
                onToggleTool={toggleTool}
                onToggleAll={toggleTools}
              />
            ))}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
